import { FC, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { ShieldCheck, CheckCheck, XCircle } from 'lucide-react';
import { useAuth } from '../../modules/auth';
import { usePermissions } from '../../modules/auth/hooks/usePermissions';
import { PageCard, SectionLabel, TOKENS, ConfirmDialog } from '../../components/design-system';
import axios from 'axios';
import { listAdminPendingStock, batchActivateStock, revokeStock } from './api';
import { listManufacturers } from '../manufacturers-admin/api';
import { StockStatus, STOCK_STATUS_LABEL, ManufacturerStock } from './types';
import { ActivateStockModal } from './components/ActivateStockModal';

const API_URL = process.env.REACT_APP_API_URL;

// Companies endpoint already used by the existing admin pages.
// Returns { data: [{ id, name }] } from /companies endpoint.
async function fetchCompanies(): Promise<Array<{ id: number; name: string }>> {
  const { data } = await axios.get<{ data?: Array<{ id: number; name: string }> } | Array<{ id: number; name: string }>>(`${API_URL}/companies`);
  // Tolerate either shape — some legacy endpoints return the array directly.
  const list = Array.isArray(data) ? data : (data?.data ?? []);
  return list.map((c: any) => ({ id: Number(c.id), name: String(c.name ?? `#${c.id}`) }));
}

const PAGE_SIZE = 50;

export const AdminManufacturerStockPage: FC = () => {
  const { currentUser } = useAuth();
  const perms = usePermissions();
  const defaultCompanyId = currentUser?.last_company_id ? +currentUser.last_company_id : 0;

  const [statusFilter, setStatusFilter] = useState<StockStatus>('pending');
  const [manufacturerFilter, setManufacturerFilter] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [batchCompany, setBatchCompany] = useState('');
  const [confirmBatch, setConfirmBatch] = useState(false);
  const [revokeId, setRevokeId] = useState<number | null>(null);
  // Single-activate: opens the rich ActivateStockModal pre-loaded with
  // the box payload so the admin fills empresa + projeto + dados
  // operacionais antes de salvar.
  const [activateOne, setActivateOne] = useState<ManufacturerStock | null>(null);

  const companiesQuery = useQuery(
    ['admin-companies-for-activation'],
    fetchCompanies,
    { enabled: perms.is_admin_anywhere, staleTime: 60_000 },
  );
  const companies = companiesQuery.data ?? [];

  // Load manufacturers for the filter dropdown. Quick fetch; the admin
  // typically has a handful so we don't bother with paging here.
  const manufacturersQuery = useQuery(
    ['admin-manufacturers-for-filter'],
    () => listManufacturers({ take: 200 }),
    { enabled: perms.is_admin_anywhere, staleTime: 60_000 },
  );

  const qc = useQueryClient();
  const list = useQuery(
    ['admin-manufacturer-stock', statusFilter, manufacturerFilter, search, page],
    () => listAdminPendingStock({
      status: statusFilter,
      manufacturer_id: manufacturerFilter ?? undefined,
      search: search.trim() || undefined,
      take: PAGE_SIZE,
      skip: (page - 1) * PAGE_SIZE,
    }),
    { enabled: perms.is_admin_anywhere, keepPreviousData: true },
  );

  // Single-activate moved entirely into ActivateStockModal — the modal
  // owns its own mutation so it can pass the full box payload. We just
  // pop it open with the stock row.

  const batchMut = useMutation(
    (items: Array<{ stock_id: number; company_id: number }>) => batchActivateStock(items),
    {
      onSuccess: (data) => {
        if (data.errors.length > 0) {
          toast.warning(`${data.activated.length} ativada(s). ${data.errors.length} erro(s).`);
        } else {
          toast.success(`${data.activated.length} caixa(s) ativada(s).`);
        }
        setSelected(new Set());
        setBatchCompany('');
        setConfirmBatch(false);
        qc.invalidateQueries(['admin-manufacturer-stock']);
      },
      onError: (e: any) => { toast.error(`Erro: ${e?.response?.data?.message ?? e.message}`); },
    },
  );

  const revokeMut = useMutation(
    (id: number) => revokeStock(id),
    {
      onSuccess: () => { toast.success('Caixa revogada.'); setRevokeId(null); qc.invalidateQueries(['admin-manufacturer-stock']); },
      onError: (e: any) => { toast.error(`Erro: ${e?.response?.data?.message ?? e.message}`); },
    },
  );

  if (!perms.is_admin_anywhere) {
    return (
      <PageCard>
        <SectionLabel>Aprovação de caixas</SectionLabel>
        <div style={{ padding: 16, color: TOKENS.color.muted, fontSize: 13 }}>
          Apenas administradores de empresa podem aprovar caixas em estoque.
        </div>
      </PageCard>
    );
  }

  const rows = list.data?.rows ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const selectAllOnPage = () => {
    const next = new Set(selected);
    let allSelected = rows.every((r) => next.has(r.id));
    if (allSelected) {
      rows.forEach((r) => next.delete(r.id));
    } else {
      rows.forEach((r) => next.add(r.id));
    }
    setSelected(next);
  };

  const handleBatch = () => {
    const company_id = Number(batchCompany);
    if (!company_id) { toast.error('Informe a empresa destino.'); return; }
    const items = Array.from(selected).map((stock_id) => ({ stock_id, company_id }));
    batchMut.mutate(items);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: TOKENS.spacing.md }}>
      <PageCard>
        <SectionLabel>Administração</SectionLabel>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          <ShieldCheck size={20} color={TOKENS.color.action} />
          <h1 style={{ fontSize: 22, fontWeight: 700, color: TOKENS.color.text, margin: 0 }}>Aprovação de caixas</h1>
        </div>
        <p style={{ marginTop: 8, fontSize: 13, color: TOKENS.color.muted, lineHeight: 1.5, marginBottom: 0 }}>
          Caixas em estoque cadastradas pelos fabricantes aguardando ativação. Ao aprovar,
          informe a empresa destino — a caixa é criada como ativa naquele momento.
        </p>
      </PageCard>

      <PageCard>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {(['pending', 'activated', 'revoked'] as StockStatus[]).map((s) => {
            const active = statusFilter === s;
            return (
              <span key={s} role='button' onClick={() => { setStatusFilter(s); setPage(1); setSelected(new Set()); }} style={{
                padding: '4px 10px', borderRadius: TOKENS.radius.chip, fontSize: 11, fontWeight: 700,
                letterSpacing: 0.3, textTransform: 'uppercase', cursor: 'pointer',
                background: active ? TOKENS.color.action : TOKENS.color.surface,
                color: active ? '#fff' : TOKENS.color.text,
              }}>{STOCK_STATUS_LABEL[s]}</span>
            );
          })}
        </div>

        <div style={{ display: 'flex', gap: 6, marginTop: TOKENS.spacing.sm, flexWrap: 'wrap' }}>
          <input
            type='text'
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder='🔍 Buscar por CMB ou bluetooth'
            style={{
              flex: 1, minWidth: 220, padding: '8px 10px', fontSize: 13,
              border: `1px solid ${TOKENS.color.border}`, borderRadius: TOKENS.radius.chip,
              background: TOKENS.color.card, color: TOKENS.color.text, outline: 'none',
            }}
          />
          <select
            value={manufacturerFilter ?? ''}
            onChange={(e) => { setManufacturerFilter(e.target.value ? Number(e.target.value) : null); setPage(1); }}
            style={{
              padding: '8px 10px', fontSize: 13,
              border: `1px solid ${TOKENS.color.border}`, borderRadius: TOKENS.radius.chip,
              background: TOKENS.color.card, color: TOKENS.color.text, outline: 'none',
              minWidth: 200,
            }}
          >
            <option value=''>Todos os fabricantes</option>
            {(manufacturersQuery.data?.rows ?? []).map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>

        {/* Batch action bar — only shown when items are selected */}
        {statusFilter === 'pending' && selected.size > 0 && (
          <div style={{
            marginTop: TOKENS.spacing.sm, padding: 10,
            background: TOKENS.color.actionSoft, border: `1px dashed ${TOKENS.color.action}`,
            borderRadius: TOKENS.radius.chip,
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: TOKENS.color.action }}>
              {selected.size} selecionada(s)
            </span>
            <select
              value={batchCompany}
              onChange={(e) => setBatchCompany(e.target.value)}
              style={{
                padding: '6px 10px', fontSize: 12,
                border: `1px solid ${TOKENS.color.border}`, borderRadius: TOKENS.radius.chip,
                background: TOKENS.color.card, color: TOKENS.color.text, outline: 'none',
                minWidth: 220,
              }}
            >
              <option value=''>Selecione a empresa destino…</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <button
              type='button'
              disabled={!batchCompany || batchMut.isLoading}
              onClick={() => setConfirmBatch(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: TOKENS.color.action, color: '#fff', border: 'none',
                padding: '6px 12px', fontSize: 12, fontWeight: 700, borderRadius: TOKENS.radius.chip,
                cursor: (!batchCompany || batchMut.isLoading) ? 'not-allowed' : 'pointer',
                opacity: (!batchCompany || batchMut.isLoading) ? 0.5 : 1,
              }}
            >
              <CheckCheck size={14} /> Ativar selecionadas
            </button>
            <button type='button' onClick={() => setSelected(new Set())} style={{
              background: 'transparent', border: 'none', color: TOKENS.color.muted, fontSize: 12, cursor: 'pointer',
            }}>Limpar seleção</button>
          </div>
        )}

        <div style={{ marginTop: TOKENS.spacing.md }}>
          {list.isLoading ? (
            <div style={{ padding: 24, textAlign: 'center', color: TOKENS.color.muted, fontSize: 13 }}>Carregando…</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: TOKENS.color.muted, fontSize: 13 }}>
              Nenhuma caixa em estoque com esses filtros.
            </div>
          ) : (
            <>
              {/* Select-all checkbox */}
              {statusFilter === 'pending' && (
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, color: TOKENS.color.muted, marginBottom: 6 }}>
                  <input
                    type='checkbox'
                    checked={rows.length > 0 && rows.every((r) => selected.has(r.id))}
                    onChange={selectAllOnPage}
                  />
                  Selecionar todas desta página
                </label>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {rows.map((r) => {
                  const isSelected = selected.has(r.id);
                  return (
                    <div key={r.id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 12px', background: isSelected ? TOKENS.color.actionSoft : TOKENS.color.surface,
                      borderRadius: TOKENS.radius.chip, fontSize: 12,
                      borderLeft: `3px solid ${
                        r.status === 'activated' ? TOKENS.color.severity.low.text
                        : r.status === 'revoked' ? TOKENS.color.muted
                        : TOKENS.color.severity.medium.text
                      }`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {statusFilter === 'pending' && (
                          <input type='checkbox' checked={isSelected} onChange={() => toggle(r.id)} />
                        )}
                        <div>
                          <div style={{ fontWeight: 700, color: TOKENS.color.text }}>
                            CMB {r.cmb_number} <span style={{ color: TOKENS.color.muted, fontWeight: 500 }}>·</span> {r.bluetooth}
                          </div>
                          <div style={{ fontSize: 11, color: TOKENS.color.muted, marginTop: 2 }}>
                            Fabricante: {r.manufacturer_name ?? `#${r.manufacturer_id}`}
                            {r.intended_company_name && ` · Destino sugerido: ${r.intended_company_name}`}
                            {' · '}
                            Criada {new Date(r.created_at).toLocaleDateString('pt-BR')}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {r.status === 'pending' && (
                          <>
                            <button
                              type='button'
                              onClick={() => setActivateOne(r)}
                              style={primaryBtn}
                            >
                              Ativar
                            </button>
                            <button type='button' onClick={() => setRevokeId(r.id)} style={dangerGhost}>
                              <XCircle size={12} /> Revogar
                            </button>
                          </>
                        )}
                        {r.status === 'activated' && r.activated_to_company_name && (
                          <span style={{ fontSize: 10, fontWeight: 600, color: TOKENS.color.muted }}>
                            → {r.activated_to_company_name}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: TOKENS.spacing.sm }}>
            <button type='button' onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} style={ghostBtn}>‹ Anterior</button>
            <span style={{ fontSize: 11, fontWeight: 700, color: TOKENS.color.muted, letterSpacing: 0.4 }}>
              {page} / {totalPages} · {total} caixas
            </span>
            <button type='button' onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={ghostBtn}>Próxima ›</button>
          </div>
        )}
      </PageCard>

      {activateOne && (
        <ActivateStockModal
          stock={activateOne}
          companies={companies}
          defaultCompanyId={defaultCompanyId}
          onClose={() => setActivateOne(null)}
        />
      )}

      <ConfirmDialog
        open={confirmBatch}
        title={`Ativar ${selected.size} caixa(s)?`}
        description={`Todas serão associadas à empresa "${companies.find((c) => c.id === Number(batchCompany))?.name ?? '#' + batchCompany}". As caixas serão criadas como ativas e prontas pra uso imediato.`}
        confirmLabel='Confirmar ativação'
        tone='danger'
        loading={batchMut.isLoading}
        onCancel={() => setConfirmBatch(false)}
        onConfirm={handleBatch}
      />

      <ConfirmDialog
        open={revokeId !== null}
        title='Revogar caixa?'
        description='O token será invalidado permanentemente. Use pra caixas defeituosas ou que não devem ser ativadas.'
        confirmLabel='Revogar'
        tone='danger'
        loading={revokeMut.isLoading}
        onCancel={() => setRevokeId(null)}
        onConfirm={() => revokeId && revokeMut.mutate(revokeId)}
      />
    </div>
  );
};

const primaryBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  background: TOKENS.color.action, color: '#fff', border: 'none',
  padding: '5px 10px', fontSize: 11, fontWeight: 700, borderRadius: TOKENS.radius.chip, cursor: 'pointer',
};
const ghostBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  background: TOKENS.color.card, color: TOKENS.color.text,
  border: `1px solid ${TOKENS.color.border}`,
  padding: '6px 12px', fontSize: 12, fontWeight: 600,
  borderRadius: TOKENS.radius.chip, cursor: 'pointer',
};
const dangerGhost: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  background: 'transparent', border: 'none',
  color: TOKENS.color.severity.high.text,
  fontSize: 11, fontWeight: 600, cursor: 'pointer',
};

export default AdminManufacturerStockPage;
